import { useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField, SelectField, FieldError } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow, StatusBadge, InfoAlert } from '../components/States.jsx';

import useForm from '../hooks/useForm.js';
import {
  useListUsersQuery,
  useListModulesQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useToggleUserMutation,
  useApproveUserMutation,
  useSetUserPasswordMutation,
  useDeleteUserMutation,
} from '../store/apiSlice.js';
import { userSchema, userCreateSchema, MIN_PASSWORD } from '../validation/schemas.js';
import { ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS, defaultModulesFor } from '../validation/roles.js';
import { useAuth } from '../store/useAuth.js';
import { useToast } from '../context/ToastContext.jsx';
import { fmtDate, dash } from '../utils/format.js';

const BLANK = { name: '', email: '', phone1: '', phone2: '', role: 'user', password: '', modules: defaultModulesFor('user') };

export default function UsersPage() {
  const showToast = useToast();
  const { user: me } = useAuth();
  const { data: users, isLoading: loading, error: apiError, refetch: reload } = useListUsersQuery();
  const { data: modules } = useListModulesQuery();
  const error = apiError?.message ?? null;

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [toggleUser] = useToggleUserMutation();
  const [approveUser] = useApproveUserMutation();
  const [setUserPassword] = useSetUserPasswordMutation();
  const [deleteUser] = useDeleteUserMutation();

  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // A new user needs a password; editing one leaves the password alone.
  const form = useForm(BLANK, editingId ? userSchema : userCreateSchema);

  /**
   * Picking a role refills the module checkboxes with that role's preset. The
   * admin can then tick extras — the server takes whatever is finally submitted.
   */
  const changeRole = (role) => {
    form.setValue('role', role);
    form.setValue('modules', defaultModulesFor(role));
  };

  const toggleModule = (moduleName) => {
    const current = form.values.modules;
    form.setValue('modules', current.includes(moduleName)
      ? current.filter((m) => m !== moduleName)
      : [...current, moduleName]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    form.reset(BLANK);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.validate()) return;

    setBusy(true);
    try {
      if (editingId) {
        const { password, ...rest } = form.values;
        await updateUser({ id: editingId, payload: rest }).unwrap();
      } else {
        await createUser(form.values).unwrap();
      }
      showToast(editingId ? 'User updated' : 'User added');
      cancelEdit();
    } catch (err) {
      form.applyServerErrors(err.details);
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    form.fill({
      name: user.name,
      email: user.email,
      phone1: user.phone1,
      phone2: user.phone2,
      role: user.role || 'user',
      password: '',
      modules: [...(user.modules || [])],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const runAction = async (fn, successMessage) => {
    setBusy(true);
    try {
      const result = await fn();
      showToast(typeof successMessage === 'function' ? successMessage(result) : successMessage);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = (user) => {
    if (!window.confirm(`Delete "${user.name}"? This cannot be undone.`)) return;
    runAction(async () => {
      await deleteUser(user.id).unwrap();
      if (editingId === user.id) cancelEdit();
    }, 'User deleted');
  };

  const handleToggle = (user) =>
    runAction(() => toggleUser(user.id).unwrap(), (updated) => (updated.status === 'disabled' ? 'User disabled' : 'User enabled'));

  const handleApprove = (user) =>
    runAction(() => approveUser(user.id).unwrap(), `${user.name} can now sign in`);

  const handleResetPassword = (user) => {
    const password = window.prompt(
      `New password for ${user.name}\n\nAt least ${MIN_PASSWORD} characters, with one letter and one number.\nThey will be signed out of every device.`
    );
    if (!password) return;
    runAction(() => setUserPassword({ id: user.id, password }).unwrap(), `Password reset for ${user.name}`);
  };

  const moduleError = form.submitted ? form.errors.modules : '';
  const pending = (users || []).filter((u) => u.status === 'pending');
  const roleHint = ROLE_DESCRIPTIONS[form.values.role];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="User Management"
        subtitle="Create staff accounts, set their role, and approve anyone who registered themselves."
      />

      {pending.length > 0 && (
        <Card title="Waiting for Approval" tag={`${pending.length} pending`}>
          <InfoAlert>
            These people registered themselves. They cannot sign in until you approve them. Set the right role first —
            everyone starts as a read-only user.
          </InfoAlert>
          <div style={{ overflowX: 'auto' }}>
            <table className="list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Registered</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{dash(user.email)}</td>
                    <td className="nowrap">{dash(user.phone1)}</td>
                    <td className="nowrap">{fmtDate(user.createdAt)}</td>
                    <td className="action-col">
                      <div className="row-actions">
                        <button type="button" className="action-btn" disabled={busy} onClick={() => startEdit(user)}>
                          Set role
                        </button>
                        <button type="button" className="action-btn primary" disabled={busy} onClick={() => handleApprove(user)}>
                          Approve
                        </button>
                        <button type="button" className="action-btn danger" disabled={busy} onClick={() => handleDelete(user)}>
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title={editingId ? 'Edit User' : 'Add User'} tag="Users">
        {editingId && (
          <div className="editing-bar">
            <span>Editing “{form.values.name}”</span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField label="Name" placeholder="Full name" maxLength={60} {...form.fieldProps('name')} />
            <TextField type="email" label="Email ID" placeholder="name@email.com" maxLength={120} {...form.fieldProps('email')} />
            <TextField label="Phone Number" placeholder="+91 98290 00000" {...form.fieldProps('phone1')} />
            <TextField label="Alternate Phone" placeholder="Optional" {...form.fieldProps('phone2')} />

            <SelectField
              label="Role"
              hint={roleHint}
              {...form.fieldProps('role')}
              onChange={(event) => changeRole(event.target.value)}
              disabled={editingId === me.id}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </SelectField>

            {!editingId && (
              <TextField
                type="password"
                label="Password"
                placeholder={`At least ${MIN_PASSWORD} characters`}
                autoComplete="new-password"
                hint="They can change it later from My Account."
                {...form.fieldProps('password')}
              />
            )}

            {editingId === me.id && (
              <div className="field full">
                <InfoAlert>You cannot change your own role or disable your own account.</InfoAlert>
              </div>
            )}

            <fieldset className="field full module-fieldset" aria-invalid={moduleError ? 'true' : undefined}>
              <legend>
                Module Access<span className="req" aria-hidden="true"> *</span>
              </legend>
              <p className="muted" style={{ fontSize: 11.5, margin: '0 0 8px' }}>
                Choosing a role ticks its usual modules. Adjust them here if this person needs something extra.
                {form.values.role === 'admin' && ' Admins can reach everything regardless of what is ticked.'}
              </p>
              <div className="module-grid">
                {(modules || []).map((moduleName) => {
                  const checked = form.values.modules.includes(moduleName);
                  return (
                    <label key={moduleName} className={`module-pill${checked ? ' on' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleModule(moduleName)} />
                      {moduleName}
                    </label>
                  );
                })}
              </div>
              <FieldError message={moduleError} />
            </fieldset>
          </div>
          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={busy}>
              {editingId ? 'Update User' : 'Add User'}
            </button>
          </div>
        </form>
      </Card>

      <Card title="User List" tag="All Users">
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="list-table">
              <thead>
                <tr>
                  <th className="sr-col">Sr. No</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Modules</th>
                  <th>Last Updated</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {!users?.length ? (
                  <EmptyRow colSpan={8}>Add your first user above</EmptyRow>
                ) : (
                  users.map((user, index) => {
                    const isMe = user.id === me.id;
                    return (
                      <tr key={user.id} className={user.status === 'disabled' ? 'row-disabled' : ''}>
                        <td className="sr-col">{index + 1}</td>
                        <td>
                          {user.name} {isMe && <span className="badge">You</span>} <StatusBadge status={user.status} />
                        </td>
                        <td className="nowrap">{ROLE_LABELS[user.role] || dash(user.role)}</td>
                        <td>{dash(user.email)}</td>
                        <td className="nowrap">{dash(user.phone1)}</td>
                        <td>{user.role === 'admin' ? 'Everything' : user.modules?.join(', ') || '—'}</td>
                        <td className="nowrap">{fmtDate(user.updatedAt)}</td>
                        <td className="action-col">
                          <div className="row-actions">
                            <button type="button" className="action-btn" disabled={busy} onClick={() => startEdit(user)}>
                              Edit
                            </button>
                            <button type="button" className="action-btn" disabled={busy} onClick={() => handleResetPassword(user)}>
                              Password
                            </button>
                            {/* You cannot lock yourself out of your own system. */}
                            {!isMe && (
                              <>
                                <button type="button" className="action-btn" disabled={busy} onClick={() => handleToggle(user)}>
                                  {user.status === 'disabled' ? 'Enable' : 'Disable'}
                                </button>
                                <button type="button" className="action-btn danger" disabled={busy} onClick={() => handleDelete(user)}>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
